/**
 * useDraftingVersions — the version history of one drafted document, whole.
 *
 * This state used to be split down the middle. `DraftingVersionsRail` owned
 * the LIST (versions, loading, availability, which restore is in flight) and
 * `DraftingDetail` owned the PREVIEW (which version is being read, its
 * content, whether that content is loading) plus a `refreshKey` counter it
 * bumped to make the rail refetch — so "restore" had to travel down as a
 * callback, back up as `onRestored`, and out again as a key bump, and nothing
 * could say how many versions there were without mounting the list.
 *
 * One hook owns all of it, which is what lets the rail pill carry a COUNT and
 * what makes `refresh()` a call instead of a counter.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import {
  fetchDraftingVersions,
  fetchDraftingVersionContent,
  restoreDraftingVersion,
  DraftingKind,
  DraftingVersion,
} from '../../../store/slices/draftingSlice';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';

export interface UseDraftingVersionsOptions {
  kind?: DraftingKind;
  uuid?: string;
  /** Only fetch once the document actually has versions — i.e. it completed. */
  enabled: boolean;
  /** A restore makes the chosen version current: reload the live document. */
  onRestored: () => void;
}

export interface DraftingVersionsState {
  versions: DraftingVersion[];
  /** How many snapshots exist — the rail pill's badge. */
  count: number;
  loading: boolean;
  /** False when the blob container has no versioning for this document. */
  available: boolean;
  /** The version being read in the canvas; null = the live document. */
  previewVersionId: string | null;
  previewContent: string | null;
  previewLoading: boolean;
  /** The version whose restore is in flight, for a per-row pending state. */
  restoringId: string | null;
  /** Preview a version, or pass null to go back to the live document. */
  selectVersion: (versionId: string | null) => Promise<void>;
  restore: (versionId: string) => Promise<void>;
  /** Refetch the list — after a regeneration writes a new snapshot. */
  refresh: () => void;
}

export function useDraftingVersions({
  kind,
  uuid,
  enabled,
  onRestored,
}: UseDraftingVersionsOptions): DraftingVersionsState {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [versions, setVersions] = useState<DraftingVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled || !kind || !uuid) return;
    setLoading(true);
    const res = await dispatch(fetchDraftingVersions({ kind, uuid }));
    if (fetchDraftingVersions.fulfilled.match(res)) {
      setVersions(res.payload.versions);
      setAvailable(res.payload.versioningAvailable || res.payload.versions.length > 0);
    }
    setLoading(false);
  }, [dispatch, enabled, kind, uuid]);

  useEffect(() => { load(); }, [load]);

  /* A new document is a new history — and never a preview of the old one. */
  useEffect(() => {
    setPreviewVersionId(null);
    setPreviewContent(null);
  }, [uuid]);

  const selectVersion = useCallback(async (versionId: string | null) => {
    if (!kind || !uuid) return;
    if (versionId === null) {
      setPreviewVersionId(null);
      setPreviewContent(null);
      return;
    }
    setPreviewVersionId(versionId);
    setPreviewLoading(true);
    const res = await dispatch(fetchDraftingVersionContent({ kind, uuid, versionId }));
    setPreviewLoading(false);
    if (fetchDraftingVersionContent.fulfilled.match(res)) {
      setPreviewContent(res.payload.content);
    } else {
      setPreviewContent(null);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: (res.payload as string) || t('Failed to load version'),
        showModal: true,
        duration: 4000,
      }));
    }
  }, [dispatch, kind, uuid, t]);

  const restore = useCallback(async (versionId: string) => {
    if (!kind || !uuid) return;
    setRestoringId(versionId);
    const res = await dispatch(restoreDraftingVersion({ kind, uuid, versionId }));
    setRestoringId(null);
    if (restoreDraftingVersion.fulfilled.match(res)) {
      // The restored version is now current: leave the preview, reload both
      // the live document (the page's job) and the list (ours).
      setPreviewVersionId(null);
      setPreviewContent(null);
      onRestored();
      load();
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: t('Restored to the selected version.'),
        showModal: true,
        duration: 3000,
      }));
    } else {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: (res.payload as string) || t('Restore failed.'),
        showModal: true,
        duration: 4000,
      }));
    }
  }, [dispatch, kind, uuid, onRestored, load, t]);

  return {
    versions,
    count: versions.length,
    loading,
    available,
    previewVersionId,
    previewContent,
    previewLoading,
    restoringId,
    selectVersion,
    restore,
    refresh: load,
  };
}

export default useDraftingVersions;
